import type { BrowserInfo } from '@adyen/adyen-web';
import * as Sentry from '@sentry/react';
import { CardElement } from '@stripe/react-stripe-js';
import type {
  PaymentIntentResult,
  Stripe,
  StripeCardElement,
  StripeCardElementOptions,
  StripeElements,
} from '@stripe/stripe-js';
import { AxiosError } from 'axios';
import { hoursToSeconds, millisecondsToSeconds } from 'date-fns';
import { t } from 'i18next';
import { isEmpty } from 'lodash-es';
import isNumber from 'lodash-es/isNumber.js';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { isHydrated, makePersistable } from 'mobx-persist-store';
import { v4 as uuid } from 'uuid';

import {
  type DeliverySlot,
  type DeliverySlotsResponse,
  ETAPaymentMethod,
  isPhonePaymentMethod,
} from '~/api/ETA';
import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import {
  type NewOrderResponse,
  type Payment,
  PaymentSystem,
} from '~/api/Order';
import type { ApiErrorResponse } from '~/api/Requests';
import UnknownCard from '~/assets/img/unknown_card.png';
import { company } from '~/company/Company';
import { getAgeByDate } from '~/components/ModalBirthDate/utils';
import CARD_BRANDS from '~/pages/Checkout/cardBrands';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import eventEmitter, { EventsName } from '~/services/Events';
import { notificationService } from '~/services/NotificationService';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  settingStore,
} from '~/stores';
import AdditionalPaymentMethod from '~/stores/CheckoutStore/AdditionalPaymentMethod';
import type Delivery from '~/stores/CheckoutStore/Delivery';
import MobilePaymentMethod from '~/stores/CheckoutStore/MobilePaymentMethod';
import type OrderData from '~/stores/CheckoutStore/OrderData';
import type PaymentMethod from '~/stores/CheckoutStore/PaymentMethod';
import type StateData from '~/stores/CheckoutStore/StateData';
import { MetaStore } from '~/stores/shared/MetaStore';
import { SlotsChangedEventStore } from '~/stores/shared/SlotChangedEventStore';
import { formatPrice } from '~/utils/formaters';
import { clearStoredPromocode, getStoredPromocode } from '~/utils/referralLink';

import type { CartItem } from '../CartStore/interfaces';
import {
  PAYMENT_TIMEOUT,
  PAYMENT_WAITING_TIME,
  PRESENT_PROMOCODES,
} from '../constants';
import { storage as localStorage } from '../LocalStorage';
import type { RequestETAResponse } from '../OrderStore/OrderStore';
import { OrderErrorHandled } from '../OrderStore/parseOrderError';
import type { UserStore } from '../UserStore';

import type { CheckoutStorePartial } from './interfaces';
import { SlotSelectModalStore } from './SlotSelectModalStore';
import { groupByDeliverySlots, type NormalizedDeliveryItems } from './utils';

export interface AdditionalPaymentData {
  paymentMethodInfo: {
    encryptedCard?: string;
    deviceInfoId?: string;
    paymentMethodDetails?: {
      [key: string]: any;
      checkoutAttemptId?: string | undefined;
    };
    browserInfo?: BrowserInfo;
    origin?: string;
  };
  billingInfo?: {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    countryCode: string;
  };
}

export class CheckoutStore implements CheckoutStorePartial {
  addressVal = '';
  address2Val = '';
  addressFloorVal = '';
  addressBldVal = '';
  addressAptVal = '';
  phoneVal = '';
  isPhoneValid = true;
  isEmailSync = true;
  isPersonalDataSync = true;
  nameVal = '';
  emailVal = '';
  commentVal = '';
  recipientPhoneVal = '';

  isRecipientPhoneValid = false;
  recipientNameVal = '';
  recipientEmailVal = '';
  recipientTempPhoneVal = '';
  isRecipientTempPhoneValid = false;
  recipientTempNameVal = '';
  recipientTempEmailVal = '';
  agreeSaveCard = true;
  error: string | null = null;
  isLoading = false;
  isDisabled = true;
  isPersonalDataChanged = false;
  isShowRecipientPopover = false;
  isShowTimeoutPopover = false;
  isUnableConfirmCardPayment = false;
  isAddNewCard = false;
  _isCardValid = false;
  _isCardAdded = false;
  paymentIntent: NewOrderResponse['data']['orders'][number] | null = null;
  paymentIntentMultiorder: NewOrderResponse['data']['multi_order'] | null =
    null;
  paymentMethod: { card: StripeCardElement } | string = '';
  orderId = '';
  octoPaymentLink: string | null = null;
  private _sentAnalyticsOrderIds = new Set<string>();
  private _processedOrderIds = new Set<string>();
  cancellationDelay: number | null = null;
  isThreeds2Active = false;
  instructionList: Record<number, string> = {};
  activePaymentMethod: PaymentMethod | null = null;
  activePaymentSystem: PaymentSystem | null = null;
  payments: Array<{ value: string; text: string }> | null = null;
  paymentsMethodId: string | null = null;
  cardStyle: StripeCardElementOptions = {
    style: {
      base: {
        color: '#333',
        fontFamily: '"Noto Sans", sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#878786',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };
  stateData: StateData | null = null;
  isRefreshed = false;
  deliveryMethod: ETADeliveryMethodType = ETADeliveryMethodType.JiffyDelivery;
  isUpdatedDeliveryPopover = false;
  deliveryPrices: RequestETAResponse | null = null;
  useBonuses = false;
  sessionId: string | undefined = undefined;

  deliveryAllTogether = false;

  deliverySlots: DeliverySlotsResponse = {};

  isDeliverTogether = company.combineItemsByDefaut;

  hasExactAmount = false;
  changeFromAmount: Nullable<number> = null;

  mobileMoneyPhoneVal = '';

  _isNewAdyenCard: boolean | null = null;
  _isAdyenCardValid: boolean | null = null;

  // FIXME: Why is this a string?
  selectedDeliveryDate: string | null = '0';
  selectedDeliverySlot: DeliverySlot | null = null;
  deliveries: Delivery[] | null = null;
  isMultipleDeliveriesAvailable = false;

  readonly slotsChangedEventStore: SlotsChangedEventStore;
  readonly slotSelectionModalStore: SlotSelectModalStore;
  readonly deliveryMethodChangeMeta: MetaStore = new MetaStore();

  constructor(protected readonly userStore: UserStore) {
    this.slotsChangedEventStore = new SlotsChangedEventStore(this);
    this.slotSelectionModalStore = new SlotSelectModalStore(this);

    makeAutoObservable(this);
    makePersistable(this, {
      name: 'CheckoutStore',
      properties: [
        'activePaymentMethod',
        'activePaymentSystem',
        'payments',
        'deliveryMethod',
        'paymentsMethodId',
        'stateData',
        'deliveries',
        'isMultipleDeliveriesAvailable',
        'isDeliverTogether',
      ],
      storage: localStorage,
    });
  }

  // Getters
  get isSynchronized(): boolean {
    return isHydrated(this);
  }

  get isPersonalDataValid(): boolean {
    return !!(
      this.isPhoneValid &&
      this.userStore.fullName.length &&
      this.isEmailValid &&
      this.isEmailSync &&
      this.isPersonalDataSync
    );
  }

  get isRecipientValid(): boolean {
    return !!(
      this.isPersonalDataValid &&
      this.isRecipientPhoneValid &&
      this.recipientNameVal.length &&
      this.isRecipientEmailValid
    );
  }

  get isRecipientTempValid(): boolean {
    return !!(
      this.isRecipientTempPhoneValid &&
      this.recipientTempNameVal.length &&
      this.isRecipientTempEmailValid
    );
  }

  get isEmailValid(): boolean {
    return mainStore.validateEmail(this.emailVal);
  }

  get isRecipientEmailValid(): boolean {
    return mainStore.validateEmail(this.recipientEmailVal);
  }

  get isRecipientTempEmailValid(): boolean {
    return mainStore.validateEmail(this.recipientTempEmailVal);
  }

  get hasAgeRestrictionError(): boolean {
    if (!company.hasAgeRestriction) {
      return false;
    }

    if (
      (!this.userStore.isAuthorized &&
        cartStore.hasRestrictedCategoriesInCart) ||
      cartStore.isMissingBirthdayErrorCatched
    ) {
      return true;
    }

    return (
      this.userStore.isAuthorized &&
      !this.userStore.dateOfBirth &&
      cartStore.hasRestrictedCategoriesInCart
    );
  }

  get ageRestrictionErrorType():
    | 'unauthorized'
    | 'no-date'
    | 'underage'
    | null {
    if (!company.hasAgeRestriction) {
      return null;
    }

    if (
      !this.userStore.isAuthorized &&
      cartStore.hasRestrictedCategoriesInCart
    ) {
      return 'unauthorized';
    }

    if (
      (this.userStore.isAuthorized &&
        !this.userStore.dateOfBirth &&
        cartStore.hasRestrictedCategoriesInCart) ||
      cartStore.isMissingBirthdayErrorCatched
    ) {
      return 'no-date';
    }

    if (
      this.userStore.isAuthorized &&
      this.userStore.dateOfBirth &&
      getAgeByDate(new Date(this.userStore.dateOfBirth)) <
        settingStore.maxMinAge &&
      cartStore.hasRestrictedCategoriesInCart
    ) {
      return 'underage';
    }

    return null;
  }

  get isFormValid(): boolean {
    if (company.hasAddress2 && !this.address2Val.trim()) {
      return false;
    }

    return (
      (this.isPersonalDataValid || this.isRecipientValid) &&
      this.activePaymentMethod !== null &&
      !!this.addressVal.trim()
    );
  }

  get availableDeliveryMethods(): ETADeliveryMethodType[] {
    const methodList = Object.keys(
      orderStore.etaSlotCalculation?.cost ||
        orderStore.etaCalculation?.cost ||
        {},
    ) as ETADeliveryMethodType[];
    if (!methodList.length) {
      return [];
    }
    return [
      ETADeliveryMethodType.JiffyDelivery,
      ETADeliveryMethodType.ClickAndCollect,
    ].filter((method) => methodList.includes(method));
  }

  get availablePaymentMethods(): PaymentMethod[] {
    let etaMethods =
      orderStore.etaSlotCalculation?.paymentMethods[this.deliveryMethod] ||
      orderStore.etaCalculation?.paymentMethods[this.deliveryMethod] ||
      [];

    if (!etaMethods.length) {
      etaMethods =
        orderStore.etaSlotCalculation?.paymentMethods[
          ETADeliveryMethodType.JiffyDelivery
        ] ||
        orderStore.etaCalculation?.paymentMethods[
          ETADeliveryMethodType.JiffyDelivery
        ] ||
        [];
    }

    const paymentSystemMethods = orderStore.paymentMethodsMap;
    const intersectedMethods = etaMethods.filter(
      (method) =>
        paymentSystemMethods.has(method) &&
        (paymentSystemMethods.get(method)?.length ?? 0) > 0,
    );

    const availablePaymentMethods: PaymentMethod[] = [];

    for (const method of intersectedMethods) {
      availablePaymentMethods.push(method);

      if (method === ETAPaymentMethod.Card) {
        if (mainStore.isApplePay) {
          availablePaymentMethods.push(MobilePaymentMethod.ApplePay);
        }
        if (
          mainStore.isGooglePay &&
          !orderStore.paymentSystems.has(PaymentSystem.myfatoorah)
        ) {
          availablePaymentMethods.push(MobilePaymentMethod.GooglePay);
        }
      }
    }

    return availablePaymentMethods;
  }

  get sortedDeliverySlotsList(): DeliverySlot[] | null {
    if (isEmpty(this.deliverySlots)) {
      return null;
    }

    const slots = Object.values(this.deliverySlots).flat();

    return [...slots].sort((a, b) => a.delivery_min_time - b.delivery_min_time);
  }

  get hasBackorderItems(): boolean {
    return cartStore.selectedCartItems.some(
      ({ count, sellable, isBackorderAvailable }) =>
        isBackorderAvailable && count > sellable,
    );
  }

  get currentSelectedMethod(): Payment | undefined {
    return orderStore.paymentCards.find(
      (item) => item.id === this.paymentsMethodId,
    );
  }

  get isReadyForPayment() {
    return this._isCardAdded && this._isCardValid;
  }

  setIsCardValid(flag: boolean) {
    this._isCardValid = flag;
  }
  setIsCardAdded(flag: boolean) {
    this._isCardAdded = flag;
  }

  calculateDeliveries(slots: DeliverySlot[] | null) {
    if (this.deliveryMethod === ETADeliveryMethodType.ClickAndCollect) {
      this.isMultipleDeliveriesAvailable = false;
      this.deliveries = [
        {
          id: uuid(),
          itemsIds: cartStore.selectedCartItems.map((item) => item.sku),
        },
      ];
      return;
    }

    // max count of slots
    const normalizedCartItems: NormalizedDeliveryItems[] =
      this._normalizeCartItemsForDeliveries(slots, cartStore.selectedCartItems);
    this.slotSelectionModalStore.resetSelectedSlotForSplit();
    const groupedNotJoinedDeliveries =
      groupByDeliverySlots(normalizedCartItems);

    this.isMultipleDeliveriesAvailable = groupedNotJoinedDeliveries.length > 1;

    this.deliveries =
      this.isDeliverTogether && this.isMultipleDeliveriesAvailable
        ? this._getDeliveryItemWithAllCartItems(slots)
        : groupedNotJoinedDeliveries;
    if (company.isExpressDeliveryOffByDefault) {
      this.slotSelectionModalStore.setIsAutomatic(true);
    }
  }

  private _normalizeCartItemsForDeliveries(
    slots: DeliverySlot[] | null,
    cartItems?: CartItem[],
  ): NormalizedDeliveryItems[] {
    if (!cartItems) {
      return [];
    }

    const now = millisecondsToSeconds(Date.now());

    return cartItems.map((cartItem) => {
      const {
        id,
        sku,
        count,
        backorderLeadTime = 0,
        sellableWarehouses,
      } = cartItem;
      const currentWHStock = Number(
        sellableWarehouses[`${orderStore.etaCalculation?.warehouse.code}`] ||
          '0',
      );

      const backorderTime = now + hoursToSeconds(backorderLeadTime);
      const closestSlot = (slots || []).find(
        ({ delivery_min_time }) => delivery_min_time >= backorderTime,
      );

      if (currentWHStock >= count && orderStore.isExpressAvailableNow) {
        if (company.isExpressDeliveryOffByDefault) {
          const selectedSlot = (slots || []).find(
            ({ schedule_slot_id }) =>
              schedule_slot_id ===
              this.slotSelectionModalStore.selectedSlotIdForSplit,
          );

          if (selectedSlot) {
            return {
              itemId: id,
              sku: sku || '',
              slotDeliveryDetails: {
                scheduleSlotId: selectedSlot.schedule_slot_id,
                currentDate: selectedSlot.current_date,
              },
              slot: closestSlot,
            };
          }

          const firstSlot = (slots || []).at(0);
          if (firstSlot) {
            return {
              itemId: id,
              sku: sku || '',
              slotDeliveryDetails: {
                isAutomatic: true,
                scheduleSlotId: firstSlot.schedule_slot_id,
                currentDate: firstSlot.current_date,
              },
              slot: closestSlot,
            };
          }
        }

        return { itemId: id, sku: sku || '' };
      }

      // FIXME: handle the case where there is no slot available
      if (!closestSlot) {
        return { itemId: id, sku: sku || '' };
      }

      if (
        company.isExpressDeliveryOffByDefault &&
        this.slotSelectionModalStore.selectedSlotIdForSplit
      ) {
        const selectedSlot = (slots || []).find(
          ({ schedule_slot_id }) =>
            schedule_slot_id ===
            this.slotSelectionModalStore.selectedSlotIdForSplit,
        );

        if (selectedSlot && selectedSlot.delivery_min_time >= backorderTime) {
          return {
            itemId: id,
            sku: sku || '',
            slotDeliveryDetails: {
              scheduleSlotId: selectedSlot.schedule_slot_id,
              currentDate: selectedSlot.current_date,
            },
            slot: selectedSlot,
          };
        }
      }

      return {
        itemId: id,
        sku: sku || '',
        slotDeliveryDetails: {
          isAutomatic: company.isExpressDeliveryOffByDefault,
          scheduleSlotId: closestSlot.schedule_slot_id,
          currentDate: closestSlot.current_date,
        },
        slot: closestSlot,
      };
    });
  }

  private _getDeliveryItemWithAllCartItems(
    slots: Nullable<DeliverySlot[]>,
  ): Delivery[] {
    // All the slot dates use seconds for timestamps
    const now = millisecondsToSeconds(Date.now());

    const lastBackorder = cartStore.selectedCartItems.reduce(
      (acc, { backorderLeadTime = 0 }) => {
        const backorderTime = now + hoursToSeconds(backorderLeadTime);

        return Math.max(acc, backorderTime);
      },
      0,
    );

    const closestBackorderSlot = (slots ?? []).find(
      ({ delivery_min_time }) => delivery_min_time >= lastBackorder,
    );

    if (!closestBackorderSlot && company.isExpressDeliveryOffByDefault) {
      return [];
    }

    return groupByDeliverySlots(
      cartStore.selectedCartItems.map(({ sku }) => {
        return {
          sku: sku || '',
          slotDeliveryDetails: {
            isAutomatic: this.slotSelectionModalStore.isAutomatic,
            scheduleSlotId: closestBackorderSlot!.schedule_slot_id,
            currentDate: closestBackorderSlot!.current_date,
          },
          slot: closestBackorderSlot,
        };
      }),
    );
  }

  checkSlotExpirationOfExistingDeliveries(
    slots: DeliverySlot[] | null,
  ): boolean {
    if (!this.deliveries) {
      this.calculateDeliveries(slots);
      return false;
    }

    const dropDeliveries: Delivery[] = [];

    for (const delivery of this.deliveries) {
      if (!delivery.slotDeliveryDetails) {
        if (!orderStore.isExpressAvailableNow) {
          dropDeliveries.push(delivery);
        }
        continue;
      }

      const existingSlot = (slots || []).find(
        (slot) =>
          slot.schedule_slot_id ===
            delivery.slotDeliveryDetails?.scheduleSlotId &&
          slot.current_date === delivery.slotDeliveryDetails?.currentDate,
      );

      if (!existingSlot) {
        dropDeliveries.push(delivery);
      }
    }

    if (!dropDeliveries.length) {
      return false;
    }

    const expectingCartItems = cartStore.selectedCartItems.filter((item) =>
      dropDeliveries.some((d) => d.itemsIds.includes(item.sku)),
    );

    const normalizedCartItems: NormalizedDeliveryItems[] =
      this._normalizeCartItemsForDeliveries(slots, expectingCartItems);

    const existingDeliveriesNormalizedItems: NormalizedDeliveryItems[] =
      this.deliveries
        .filter((delivery) => !dropDeliveries.includes(delivery))
        .map((delivery) =>
          delivery.itemsIds.map((sku) => ({
            itemId: sku,
            sku: sku || '',
            slotDeliveryDetails: delivery.slotDeliveryDetails,
            slot: delivery.slot,
          })),
        )
        .flat();

    const newDeliveries = groupByDeliverySlots([
      ...normalizedCartItems,
      ...existingDeliveriesNormalizedItems,
    ]);

    this.isMultipleDeliveriesAvailable = newDeliveries.length > 1;
    this.deliveries =
      this.isDeliverTogether && this.isMultipleDeliveriesAvailable
        ? this._getDeliveryItemWithAllCartItems(slots)
        : newDeliveries;
    if (company.isExpressDeliveryOffByDefault) {
      this.slotSelectionModalStore.setIsAutomatic(true);
    }
    return true;
  }

  // Setters
  setDeliveries(deliveries: Delivery[] | null) {
    this.deliveries = deliveries;
  }

  setHasExactAmount(value: boolean) {
    this.changeFromAmount = null;
    this.hasExactAmount = value;
  }

  setChangeFromAmount(value: Nullable<number>) {
    this.hasExactAmount = false;
    this.changeFromAmount = value;
  }

  setMobileMoneyPhoneVal(value: string) {
    this.mobileMoneyPhoneVal = value;
  }

  setAddressVal(val: string) {
    this.addressVal = val;
  }

  setAddress2Val(val: string) {
    this.address2Val = val;
  }

  setAddressFloorVal(val: string) {
    this.addressFloorVal = val;
  }

  setAddressBldVal(val: string) {
    this.addressBldVal = val;
  }

  setAddressAptVal(val: string) {
    this.addressAptVal = val;
  }

  setPhoneVal(val: string) {
    this.phoneVal = val;
  }

  setIsPhoneValid(flag: boolean) {
    this.isPhoneValid = flag;
  }

  setIsEmailSync(flag: boolean) {
    if (flag) {
      this.setEmailVal(this.userStore.personalData.email || '');
    }
    this.isEmailSync = flag;
  }

  setIsPersonalDataSync(flag: boolean) {
    this.isPersonalDataSync = flag;
  }

  setNameVal(val: string) {
    this.nameVal = val;
  }

  setEmailVal(val: string) {
    this.emailVal = val;
  }

  setCommentVal(val: string) {
    this.commentVal = val;
  }

  setInstructionList(val: Record<number, string>) {
    this.instructionList = val;
  }

  setRecipientTempPhoneVal(val: string) {
    this.recipientTempPhoneVal = val;
  }

  setIsRecipientTempPhoneValid(flag: boolean) {
    this.isRecipientTempPhoneValid = flag;
  }

  setRecipientTempNameVal(val: string) {
    this.recipientTempNameVal = val;
  }

  setRecipientTempEmailVal(val: string) {
    this.recipientTempEmailVal = val;
  }

  setAgreeSaveCard(flag: boolean) {
    this.agreeSaveCard = flag;
  }

  setIsLoading(flag: boolean) {
    this.isLoading = flag;
  }

  setPaymentIntent(newOrderResponse: NewOrderResponse | null) {
    if (!newOrderResponse) {
      this.paymentIntentMultiorder = null;
      this.paymentIntent = null;
      return;
    }

    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    this.paymentIntentMultiorder = newOrderResponse.data.multi_order;
    this.paymentIntent = order;
  }

  setCancellationDelay(delay: number | null) {
    this.cancellationDelay = delay;
  }

  setIsThreeds2Active(isActive: boolean) {
    this.isThreeds2Active = isActive;
  }

  setPaymentMethod(val: { card: StripeCardElement } | string) {
    this.paymentMethod = val;
  }

  setOrderId(val: string) {
    this.orderId = val;
  }

  setError(val: string | null) {
    if (val) {
      mainStore.pushAlert('error', val);
    }

    this.error = val;
  }

  setIsDisabled(flag: boolean) {
    this.isDisabled = flag;
  }

  setIsPersonalDataChanged(flag: boolean) {
    this.isPersonalDataChanged = flag;
  }

  setIsShowRecipientPopover(flag: boolean) {
    this.isShowRecipientPopover = flag;
  }

  setIsUnableConfirmCardPayment(flag: boolean) {
    this.isUnableConfirmCardPayment = flag;
  }

  setRecipient() {
    this.recipientPhoneVal = toJS(this.recipientTempPhoneVal);
    this.isRecipientPhoneValid = toJS(this.isRecipientTempPhoneValid);
    this.recipientNameVal = toJS(this.recipientTempNameVal);
    this.recipientEmailVal = toJS(this.recipientTempEmailVal);
  }

  addInstruction(val: string, index: number) {
    this.instructionList[index] = val;
  }

  deleteInstruction(index: number) {
    delete this.instructionList[index];
  }

  resetInstructionList() {
    this.instructionList = {};
  }

  setActivePaymentMethod(
    paymentMethod: PaymentMethod | null,
    options?: { skipAnalytics?: boolean },
  ) {
    this.activePaymentMethod = paymentMethod;

    if (
      Object.values(ETAPaymentMethod).includes(
        paymentMethod as ETAPaymentMethod,
      )
    ) {
      this.activePaymentSystem =
        orderStore.paymentMethodsMap.get(
          paymentMethod as ETAPaymentMethod,
        )?.[0] || null;
    } else if (
      paymentMethod === MobilePaymentMethod.ApplePay ||
      paymentMethod === MobilePaymentMethod.GooglePay
    ) {
      const cardSystems =
        orderStore.paymentMethodsMap.get(ETAPaymentMethod.Card) ?? [];
      const walletSystems: PaymentSystem[] = [
        PaymentSystem.stripe,
        PaymentSystem.adyen,
        PaymentSystem.verifone,
        PaymentSystem.myfatoorah,
      ];
      this.activePaymentSystem =
        cardSystems.find((sys) => walletSystems.includes(sys)) ??
        cardSystems[0] ??
        null;
    } else {
      this.activePaymentSystem = null;
    }

    this.setError(null);
    eventEmitter.emit(EventsName.PAYMENT_METHOD_CHANGED);

    if (!options?.skipAnalytics) {
      analyticsService.track(AnalyticsEvent.PAYMENT_METHOD_SELECTED, {
        payment_method: paymentMethod,
        payment_system: this.activePaymentSystem,
      });
    }
  }

  setIsAddNewCard(flag: boolean) {
    this.isAddNewCard = flag;
    this.setIsDisabled(flag);
    this.setError(null);
  }

  setPayments(val: { value: string; text: string }[] | null) {
    this.payments = val;
  }

  setPaymentsMethodId(val: string | null) {
    this.paymentsMethodId = val;
  }

  setIsShowTimeoutPopover(flag: boolean) {
    this.isShowTimeoutPopover = flag;
  }

  setDeliveryMethod(method: ETADeliveryMethodType) {
    if (this.deliveryMethod !== method) {
      this.deliveryMethodChangeMeta.setLoading(true);
    }
    // this assign will trigger reaction that runs calculate deliveries
    this.deliveryMethod = method;
  }

  setIsUpdatedDeliveryPopover(flag: boolean) {
    this.isUpdatedDeliveryPopover = flag;
  }

  setDeliveryPrices(val: RequestETAResponse) {
    this.deliveryPrices = val;
  }

  setUseBonuses(flag: boolean) {
    this.useBonuses = flag;
  }

  setSessionId(val: string) {
    this.sessionId = val;
  }

  setDeliverySlots(slots: DeliverySlotsResponse) {
    this.deliverySlots = slots;
  }
  setDeliveryAllTogether(val: boolean): void {
    this.deliveryAllTogether = val;
  }

  setSelectedDate(val: string) {
    this.selectedDeliveryDate = val;
    if (this.deliverySlots && val !== '0') {
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      this.setSelectedSlot(this.deliverySlots[val][0]);
    } else {
      this.setSelectedSlot(null);
    }
  }

  setSeletectedDateExpress() {
    this.selectedDeliveryDate = '0';
    this.selectedDeliverySlot = null;
  }

  // TODO: is it obsolete now?
  async setSelectedSlot(val: DeliverySlot | null) {
    this.selectedDeliverySlot = val;

    await orderStore.requestETA();
    await cartStore.calculateCart();
  }

  setIsNewAdyenCard(flag: boolean) {
    this._isNewAdyenCard = flag;
  }

  setIsAdyenCardValid(flag: boolean) {
    this._isAdyenCardValid = flag;
  }

  calculateFullDeliveryFee() {
    let fullDeliveryPrice = 0;

    this.deliveries?.forEach((_, index) => {
      const order = cartStore.deliveriesOrders[index];
      if (!order) {
        return;
      }
      if (order.is_delivery_free) {
        return;
      }

      const deliveryPrice = formatPrice(
        mainStore.convertPenceToPounds(order.delivery_info.paid_price),
      );

      fullDeliveryPrice = +fullDeliveryPrice + +deliveryPrice;
    });

    return `${fullDeliveryPrice}`;
  }

  // Actions
  getOrderData(additionalData?: AdditionalPaymentData): OrderData {
    let instructions = Object.values(this.instructionList);

    let customerCash = 0;
    const hasExactAmount = this.hasExactAmount;
    if (hasExactAmount) {
      instructions.push(this.getExactAmountInstruction());
    } else if (isNumber(this.changeFromAmount)) {
      customerCash = Math.abs(Number(this.changeFromAmount.toFixed(2)));
    }

    const notes = [
      this.address2Val,
      instructions.join(', ').toUpperCase(),
      this.commentVal,
    ]
      .filter((i) => i)
      .join(', ');
    if (this.deliveryMethod === ETADeliveryMethodType.ClickAndCollect) {
      instructions = [];
    }
    let isSaveCard = toJS(this.agreeSaveCard);
    if (
      mainStore.isRN &&
      this.activePaymentMethod === MobilePaymentMethod.ApplePay &&
      mainStore.isApplePay
    ) {
      isSaveCard = false;
    }
    if (
      mainStore.isRN &&
      this.activePaymentMethod === MobilePaymentMethod.GooglePay &&
      mainStore.isGooglePay
    ) {
      isSaveCard = false;
    }

    return {
      address: this.addressVal,
      address2: this.address2Val,
      addressFloor: this.addressFloorVal,
      addressBld: this.addressBldVal,
      addressApt: this.addressAptVal,
      fullName: this.isRecipientValid
        ? this.recipientNameVal
        : this.userStore.fullName,
      email: this.isRecipientValid
        ? this.recipientEmailVal.trim()
        : this.emailVal.trim(),
      phone: this.isRecipientValid ? this.recipientPhoneVal : this.phoneVal,
      mobileMoneyPhone: this.mobileMoneyPhoneVal.trim(),
      comment: this.commentVal,
      instructions,
      notes,
      saveCard: isSaveCard,
      deliveryMethod: this.deliveryMethod,
      sessionId: this.sessionId,
      customerCash,
      hasExactAmount,
      additionalData,
      ...(this.selectedDeliverySlot && this.selectedDeliveryDate
        ? {
            slot_delivery_details: {
              schedule_slot_id: this.selectedDeliverySlot.schedule_slot_id,
              current_date: +this.selectedDeliveryDate / 1000,
            },
          }
        : {}),
    };
  }

  switchDeliverTogether() {
    this.isDeliverTogether = !this.isDeliverTogether;
    mainStore.sendToRN('logAmplitudeEvent', {
      name: 'Delivery option changed',
      params: { type: this.isDeliverTogether ? 'single' : 'split' },
    });
    cartStore.calculateCart();
  }

  autofillRecipient() {
    this.recipientTempPhoneVal = toJS(this.phoneVal);
    this.isRecipientTempPhoneValid = toJS(this.isPhoneValid);
    this.recipientTempNameVal = this.userStore.fullName;
    this.recipientTempEmailVal = toJS(this.emailVal);
  }

  purchaseCompletedAnalytics(orderId: string) {
    if (this._sentAnalyticsOrderIds.has(orderId)) {
      return;
    }
    this._sentAnalyticsOrderIds.add(orderId);

    const lastOrderETA = orderStore.etaCalculation?.duration.min || 0;
    mainStore.sendToRN('sendTags', {
      last_ordered_time: Math.floor(Date.now() / 1000),
    });

    analyticsService.track(AnalyticsEvent.PIXEL_PURCHASE, {
      value: mainStore.toFloat(cartStore.finalPrice),
      currency: orderStore.currency.toUpperCase(),
      content_type: 'product',
      num_items: cartStore.totalCartCount,
      order_id: orderId,
    });

    analyticsService.track(AnalyticsEvent.PURCHASE_COMPLETED, {
      cart_id: undefined,
      products_amount: cartStore.cart.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      items_amount: cartStore.totalCartCount,
      price: cartStore.totalCartPrice.base,
      final_price: cartStore.finalPrice,
      eta_min: lastOrderETA,
      eta_max: orderStore.etaCalculation?.duration.max || 0,
      delivery_fee: orderStore.fee.shippingPounds || 0,
      threshold: orderStore.fee.thresholdPounds || 0,
      is_surger:
        orderStore.etaSlotCalculation?.highDemand ||
        orderStore.etaCalculation?.highDemand ||
        false,
      order_id: orderId,
      order_num: mainStore.analytics.totalSuccessfulOrders,
      promocode: cartStore.promocode.value,
      warehouse_code:
        orderStore.etaSlotCalculation?.warehouse.code ||
        orderStore.etaCalculation?.warehouse.code ||
        '',
    });
    if (mainStore.analytics.totalSuccessfulOrders === 1) {
      analyticsService.track(AnalyticsEvent.AF_FIRST_PURCHASE, {
        revenue: cartStore.finalPrice,
        price: cartStore.totalCartPrice.base,
        quantity: cartStore.totalCartCount,
        currency: orderStore.currency.toUpperCase(),
        order_id: orderId,
      });
      analyticsService.track(AnalyticsEvent.FIRST_PURCHASE, {
        transaction_id: orderId,
        value: mainStore.toFloat(cartStore.finalPrice),
        currency: orderStore.currency.toUpperCase(),
        tax: 0,
        shipping: 0,
        items: cartStore.cartForFirebase,
        coupon: cartStore.promocode.value,
      });
      mainStore.sendToRN('trackOneSignalOutcome', {
        name: 'first_purchase_quantity',
        value: cartStore.totalCartCount,
      });
      mainStore.sendToRN('trackOneSignalOutcome', {
        name: 'first_purchase_amount',
        value: cartStore.totalCartPrice.base,
      });
    } else {
      mainStore.sendToRN('trackOneSignalOutcome', {
        name: 'purchase_quantity',
        value: cartStore.totalCartCount,
      });
      mainStore.sendToRN('trackOneSignalOutcome', {
        name: 'purchase_amount',
        value: cartStore.totalCartPrice.base,
      });
    }
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last purchase date': new Date().toISOString(),
    });
    mainStore.sendToRN('setUserProperties', {
      'Commerce: last order eta': lastOrderETA,
    });
    analyticsService.track(AnalyticsEvent.PURCHASE, {
      transaction_id: orderId,
      value: mainStore.toFloat(cartStore.finalPrice),
      currency: orderStore.currency.toUpperCase(),
      tax: 0,
      shipping: 0,
      items: cartStore.cartForFirebase,
      coupon: cartStore.promocode.value,
    });
    Object.keys(this.instructionList).forEach((v) => {
      const instruction = this.instructionList[+v];
      analyticsService.track(AnalyticsEvent.DELIVERY_INSTRUCTIONS, {
        deliveryInstruction: instruction,
      });
    });
  }

  initStateData() {
    if (!this.stateData || Date.now() - this.stateData.timestamp > 6000) {
      this.stateData = null;
      this.isRefreshed = false;
      return;
    }
    const stateData = toJS(this.stateData);
    this.addressVal = stateData.addressVal;
    this.address2Val = stateData.address2Val;
    this.addressFloorVal = stateData.addressFloorVal;
    this.addressBldVal = stateData.addressBldVal;
    this.addressAptVal = stateData.addressAptVal;
    this.commentVal = stateData.commentVal;
    this.instructionList = stateData.instructionList;
    this.recipientPhoneVal = stateData.recipientPhoneVal;
    this.isRecipientPhoneValid = true;
    this.recipientNameVal = stateData.recipientNameVal;
    this.recipientEmailVal = stateData.recipientEmailVal;
    this.isAddNewCard = stateData.isAddNewCard;
    this.agreeSaveCard = stateData.agreeSaveCard;
    if (stateData.isAddNewCard) {
      this.isDisabled = true;
    }
    this.isRefreshed = true;
    this.stateData = null;
    this.deliveryMethod = stateData.deliveryMethod;
  }

  initActivePaymentMethod() {
    const silent = { skipAnalytics: true } as const;

    if (!this.availablePaymentMethods.length) {
      this.setActivePaymentMethod(null, silent);
      return;
    }

    if (
      this.activePaymentMethod &&
      this.availablePaymentMethods.includes(this.activePaymentMethod)
    ) {
      this.setActivePaymentMethod(this.activePaymentMethod, silent);
      return;
    }

    const firstPhoneMethod = this.availablePaymentMethods.find((m) =>
      isPhonePaymentMethod(m as ETAPaymentMethod),
    );
    if (firstPhoneMethod) {
      this.setActivePaymentMethod(firstPhoneMethod, silent);
      return;
    }
    if (this.availablePaymentMethods.includes(ETAPaymentMethod.QR)) {
      this.setActivePaymentMethod(ETAPaymentMethod.QR, silent);
      return;
    } else if (this.availablePaymentMethods.includes(ETAPaymentMethod.Card)) {
      this.setActivePaymentMethod(ETAPaymentMethod.Card, silent);
      return;
    } else if (this.availablePaymentMethods.includes(ETAPaymentMethod.Cash)) {
      this.setActivePaymentMethod(ETAPaymentMethod.Cash, silent);
      return;
    }
    const [first] = this.availablePaymentMethods;
    if (!first) {
      this.setActivePaymentMethod(null, silent);
      return;
    }

    this.setActivePaymentMethod(first, silent);
  }

  async requestPayments() {
    if (
      this.activePaymentSystem === PaymentSystem.myfatoorah ||
      this.activePaymentSystem === PaymentSystem.cash ||
      this.activePaymentSystem === PaymentSystem.octo
    ) {
      this.setPayments(null);
      this.setPaymentsMethodId(null);
      return;
    }

    try {
      const deliveryZoneId =
        orderStore.etaCalculation?.warehouse.zone[this.deliveryMethod]?.id;
      await orderStore
        .requestPayments(deliveryZoneId)
        .catch((error) => error && console.error(error));
      if (!orderStore.paymentCards?.length) {
        this.setPayments(null);
        this.setPaymentsMethodId(null);
        return;
      }

      const paymentList: Array<{ value: string; text: string }> =
        orderStore.paymentCards?.map((card) => {
          const {
            id,
            card: { brand, last4, exp_month, exp_year },
          } = card;

          const brandName = brand.toLowerCase();

          const cardIcon = CARD_BRANDS[brandName]?.icon ?? UnknownCard;
          const cardTitle = CARD_BRANDS[brandName]?.title ?? brand;
          const expDate =
            exp_year &&
            exp_month &&
            `${mainStore.addLeadingZero(exp_month)}/${exp_year
              .toString()
              .slice(-2)}`;

          return {
            value: id,
            text: `
             ${cardIcon ? `<img class="" src="${cardIcon}" alt="" />` : ''}
              <div class="card-main text-truncate text-capitalize">
                <p class="card-main__brand">${cardTitle}</p>
                <div class="card-main__number">${last4}</div>
              </div>
              ${expDate ? ` <div class="card-date">${expDate}</div>` : ''}
             `,
          };
        });

      const defaultIndex = orderStore.paymentCards.findIndex(
        (el) => el.metadata.is_default === 'true',
      );
      // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
      paymentList.splice(0, 0, paymentList.splice(defaultIndex || 0, 1)[0]);
      this.setPayments(paymentList);
      if (paymentList.length) {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        this.setPaymentsMethodId(paymentList[0].value);
        if (!this.isAddNewCard) {
          this.setIsDisabled(false);
        }
        analyticsService.track(
          AnalyticsEvent.PURCHASE_PAYMENT_METHOD_FILLED_IN,
          {
            cart_id: undefined,
            payment_method: 'card',
            type: 'existing',
          },
        );
        analyticsService.track(AnalyticsEvent.ADD_PAYMENT_INFO, {
          currency: orderStore.currency.toUpperCase(),
          payment_type: 'credit card',
          value: mainStore.toFloat(cartStore.finalPrice),
          items: cartStore.cartForFirebase,
          coupon: cartStore.promocode.value,
        });
      }
    } catch (error) {
      if (
        error instanceof AxiosError &&
        !error.response &&
        (error.code === 'ECONNABORTED' || error.message === 'Network Error')
      ) {
        return;
      }
      Sentry.withScope((scope) => {
        scope.setExtras({
          error: (error as AxiosError).response,
        });
        Sentry.captureMessage('[Checkout] Failed payments loading', 'warning');
      });
    }
  }

  async orderCheckout(
    stripe?: Stripe | null,
    elements?: StripeElements | null,
    additionalData?: AdditionalPaymentData,
  ) {
    try {
      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'checkout_start',
          checkoutDetails: {
            finalPrice: cartStore.finalPrice,
            pencePrice: mainStore.convertPoundsToPence(cartStore.finalPrice),
            cartItemsCount: cartStore.totalCartCount,
            cartTotalPrice: cartStore.totalCartPrice,
            hasBackorderItems: this.hasBackorderItems,
          },
          paymentDetails: {
            activePaymentMethod: this.activePaymentMethod,
            activePaymentSystem: this.activePaymentSystem,
            availablePaymentMethods: this.availablePaymentMethods,
            paymentSystem: this.activePaymentSystem,
            isStripeAvailable: !!stripe && !!elements,
            isApplePayAvailable: mainStore.isApplePay,
            isGooglePayAvailable: mainStore.isGooglePay,
          },
          deliveryDetails: {
            method: this.deliveryMethod,
            address: {
              address1: this.addressVal,
              address2: this.address2Val,
              floor: this.addressFloorVal,
              building: this.addressBldVal,
              apartment: this.addressAptVal,
            },
            etaCalculation: orderStore.etaCalculation,
          },
          validationState: {
            isPersonalDataValid: this.isPersonalDataValid,
            isFormValid: this.isFormValid,
            isPhoneValid: this.isPhoneValid,
            isEmailValid: this.isEmailValid,
          },
          promocode: cartStore.promocode,
          timestamp: new Date().toISOString(),
        });
        Sentry.captureMessage(
          '[Checkout Flow] Starting checkout process',
          'info',
        );
      });

      if (mainStore.convertPoundsToPence(cartStore.finalPrice) < 50) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'checkout_validation_failed',
            error: 'price_too_low',
            price: cartStore.finalPrice,
            pencePrice: mainStore.convertPoundsToPence(cartStore.finalPrice),
          });
          Sentry.captureMessage(
            '[Checkout Flow] Price below minimum threshold',
            'warning',
          );
        });
        mainStore.setIsZeroCartPopover(true);
        throw new Error('price < 50');
      }

      this.setPaymentIntent(null);
      this.setIsLoading(true);
      this.setIsUnableConfirmCardPayment(false);
      this.setError(null);

      await this.syncAddress();
      const isCartChanged = await cartStore.calculateCart();

      if (isCartChanged) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'cart_validation_failed',
            error: 'cart_changed',
            cartChanges: {
              oldCart: cartStore.cart.map((item) => ({
                sku: item.sku,
                name: item.name,
                count: item.count,
                price: item.price,
              })),
              newCart: cartStore.selectedCartItems.map((item) => ({
                sku: item.sku,
                name: item.name,
                count: item.count,
                price: item.price,
              })),
            },
          });
          Sentry.captureMessage(
            '[Checkout Flow] Cart changed during checkout',
            'warning',
          );
        });
        this.setIsLoading(false);
        throw new Error('cart is changed');
      }

      if (orderStore.gift && orderStore.activeGift) {
        orderStore.setIsAuthInCart(false);
        const isGiftsFetched = await orderStore.fetchGifts();
        if (!isGiftsFetched) {
          this.setIsLoading(false);
          throw new Error("Can't fetch gifts");
        }
      }

      if (this.deliveryMethod !== ETADeliveryMethodType.ClickAndCollect) {
        const deliveryPrices = await orderStore.requestETA();

        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'eta_calculation',
            etaResponse: {
              cost: orderStore.etaCalculation?.cost,
              duration: orderStore.etaCalculation?.duration,
              warehouse: orderStore.etaCalculation?.warehouse,
              highDemand: orderStore.etaCalculation?.highDemand,
              paymentMethods: orderStore.etaCalculation?.paymentMethods,
            },
            deliveryPrices: {
              oldPrice: deliveryPrices?.oldPrice,
              newPrice: deliveryPrices?.newPrice,
            },
            deliveryMethod: this.deliveryMethod,
            cartDetails: {
              itemCount: cartStore.totalCartCount,
              finalPrice: cartStore.finalPrice,
            },
          });
          Sentry.captureMessage(
            '[Checkout Flow] ETA calculation completed',
            'info',
          );
        });

        if (
          deliveryPrices &&
          deliveryPrices.oldPrice !== deliveryPrices.newPrice
        ) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'delivery_price_changed_after_retry',
              oldPrice: deliveryPrices.oldPrice,
              newPrice: deliveryPrices.newPrice,
            });
            Sentry.captureMessage(
              '[Checkout Flow] Delivery price changed after retry',
              'warning',
            );
          });
          this.setIsLoading(false);
          throw new Error('oldPrice !== newPrice');
        }

        if (!orderStore.etaCalculation) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'eta_calculation_missing',
              error: 'missing_eta_data',
            });
            Sentry.captureMessage(
              '[Checkout Flow] Missing ETA calculation data',
              'error',
            );
          });
          this.setIsLoading(false);
          throw new Error('!orderStore.etaCalculation');
        }
      }

      const activePaymentMethod = this.activePaymentMethod;
      const isDeliverySlotsChanged = await orderStore.reCalculateDeliveries();

      if (isDeliverySlotsChanged) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'delivery_slots_changed',
            error: 'slots_changed',
            oldSlots: this.deliverySlots,
          });
          Sentry.captureMessage(
            '[Checkout Flow] Delivery slots changed',
            'warning',
          );
        });
        this.setIsLoading(false);
        throw new Error('Delivery slots changed');
      }

      this.initActivePaymentMethod();
      if (activePaymentMethod !== this.activePaymentMethod) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'payment_method_changed',
            error: 'method_changed',
            oldMethod: activePaymentMethod,
            newMethod: this.activePaymentMethod,
          });
          Sentry.captureMessage(
            '[Checkout Flow] Payment method changed',
            'warning',
          );
        });
        this.setIsLoading(false);
        throw new Error('Payment method is changed');
      }

      let walletPaymentResult;

      if (this.activePaymentSystem === PaymentSystem.verifone) {
        try {
          if (this.activePaymentMethod === MobilePaymentMethod.ApplePay) {
            const data = await this.processWalletPaymentRequest('applePay');
            walletPaymentResult = data.result;
          } else if (
            this.activePaymentMethod === MobilePaymentMethod.GooglePay
          ) {
            const data = await this.processWalletPaymentRequest('googlePay');
            walletPaymentResult = data.result;
          }
        } catch (error) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              error,
              paymentMethod: this.activePaymentMethod,
              paymentAmount: cartStore.finalPrice,
            });
            Sentry.captureMessage(
              '[Wallet Payment] Payment Request API failed',
              'error',
            );
          });
          this.setIsLoading(false);
          throw error;
        }
      }

      let response: NewOrderResponse | null = null;
      try {
        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'creating_order',
            orderData: this.getOrderData(additionalData),
            slots: this.deliverySlots,
          });
          Sentry.captureMessage('[Checkout Flow] Creating new order', 'info');
        });

        // Prepare checkout data based on payment method
        let checkoutData = additionalData;

        if (walletPaymentResult) {
          const isApplePay =
            this.activePaymentMethod === MobilePaymentMethod.ApplePay;
          const isGooglePay =
            this.activePaymentMethod === MobilePaymentMethod.GooglePay;

          let walletPayload;
          let checkoutMethod;

          if (isApplePay) {
            walletPayload =
              walletPaymentResult.paymentResponse.details.applePayToken
                .paymentData;
            checkoutMethod = 'APPLE_PAY';

            Sentry.addBreadcrumb({
              category: 'payment',
              message: 'Creating order with Apple Pay wallet payload',
              level: 'info',
              data: {
                hasToken: !!walletPayload,
              },
            });
          } else if (isGooglePay) {
            walletPayload =
              walletPaymentResult.paymentResponse.details.androidPayToken
                .paymentData;
            checkoutMethod = 'GOOGLE_PAY';

            Sentry.addBreadcrumb({
              category: 'payment',
              message: 'Creating order with Google Pay wallet payload',
              level: 'info',
              data: {
                hasToken: !!walletPayload,
              },
            });
          }

          checkoutData = {
            checkoutMethod,
            ...additionalData,
            paymentMethodInfo: {
              ...additionalData?.paymentMethodInfo,
              walletPayload,
            },
          };
        }

        response = await orderStore.newOrder(this.getOrderData(checkoutData));
      } catch (error) {
        this.setIsLoading(false);
        throw error;
      }
      if (!response || !response.data.orders.length) {
        this.setIsLoading(false);
        throw new Error('No orders');
      }

      this.setPaymentIntent(response);

      if (this.activePaymentMethod !== ETAPaymentMethod.Cash) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'initiating_payment',
            paymentSystem: this.activePaymentSystem,
            paymentMethod: this.activePaymentMethod,
            orderId: response.data.orders[0]?.order?.id,
          });
          Sentry.captureMessage('[Checkout Flow] Initiating payment', 'info');
        });

        if (this.activePaymentSystem === PaymentSystem.pvit) {
          this.setIsLoading(false);
          this.setOrderId(response.data.orders[0]?.order?.id ?? '');
          return response;
        }

        if (this.activePaymentSystem === PaymentSystem.myfatoorah) {
          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.ApplePay &&
            mainStore.isApplePay
          ) {
            this.applePay(response);
            return;
          }
          if (response.data.orders[0]?.payment.myFatoorahPayment.error) {
            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'myfatoorah_error',
                error: response.data.orders[0]?.payment.myFatoorahPayment.error,
              });
              Sentry.captureMessage(
                '[Checkout Flow] MyFatoorah payment error',
                'error',
              );
            });
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            this.setIsLoading(false);
            throw new Error(
              'response.data.orders[0]?.payment.myFatoorahPayment.error',
            );
          }
          if (response.data.orders[0]?.payment?.myFatoorahPayment.paymentURL) {
            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'myfatoorah_redirect',
                paymentUrl:
                  response.data.orders[0]?.payment?.myFatoorahPayment
                    .paymentURL,
              });
              Sentry.captureMessage(
                '[Checkout Flow] Redirecting to MyFatoorah payment',
                'info',
              );
            });
            if (mainStore.isRN) {
              mainStore.sendToRN('openInAppBrowser', {
                url: response.data.orders[0]?.payment?.myFatoorahPayment
                  .paymentURL,
              });
            } else {
              window
                .open(
                  response.data.orders[0]?.payment?.myFatoorahPayment
                    .paymentURL,
                  '_blank',
                )
                ?.focus();
            }
          }
        }

        if (this.activePaymentSystem === PaymentSystem.octo) {
          const paymentLink =
            response.data.orders[0]?.payment?.providerPaymentDetails
              ?.paymentLink;

          if (paymentLink) {
            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'octo_iframe',
                paymentLink,
              });
              Sentry.captureMessage(
                '[Checkout Flow] Opening Octo payment iframe',
                'info',
              );
            });

            this.octoPaymentLink = paymentLink;

            if (mainStore.isRN) {
              mainStore.sendToRN('openInAppBrowser', {
                url: paymentLink,
              });
            }
          }

          this.setIsLoading(false);
          this.setOrderId(response.data.orders[0]?.order?.id ?? '');
          return response;
        }

        if (
          this.activePaymentSystem === PaymentSystem.midtrans &&
          response.data.orders[0]?.payment.midtransPayment
        ) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'midtrans_payment',
              token: response.data.orders[0]?.payment.midtransPayment?.token,
            });
            Sentry.captureMessage(
              '[Checkout Flow] Starting Midtrans payment',
              'info',
            );
          });
          this.midtransPay(
            response.data.orders[0].payment.midtransPayment?.token,
          );
          return;
        }

        if (
          this.activePaymentSystem === PaymentSystem.amazon &&
          this.activePaymentMethod === ETAPaymentMethod.Card
        ) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'amazon_payment',
              orderId: response.data.orders[0]?.order?.id,
            });
            Sentry.captureMessage(
              '[Checkout Flow] Starting Amazon payment',
              'info',
            );
          });
          this.setIsLoading(false);
          return;
        }

        if (!response.data.orders[0]?.payment_intent) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'payment_intent_missing',
              error: 'no_payment_intent',
              orderId: response.data.orders[0]?.order?.id,
            });
            Sentry.captureMessage(
              '[Checkout Flow] Missing payment intent',
              'error',
            );
          });
          this.setIsLoading(false);
          throw new Error('!response.data.orders[0]?.payment_intent');
        }

        if (this.activePaymentSystem === PaymentSystem.verifone) {
          // For wallet payments with Payment Request API, finalize immediately
          if (
            walletPaymentResult &&
            (this.activePaymentMethod === MobilePaymentMethod.ApplePay ||
              this.activePaymentMethod === MobilePaymentMethod.GooglePay)
          ) {
            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'verifone_wallet_payment_finalize',
                paymentMethod: this.activePaymentMethod,
                orderId: response.data.orders[0]?.order?.id,
              });
              Sentry.captureMessage(
                '[Checkout Flow] Finalizing Verifone wallet payment',
                'info',
              );
            });

            await this.finalizePayment().catch(
              (error) => error && console.error(error),
            );
            this.setIsLoading(false);
            return;
          }

          // For card payments, return response for further processing
          if (
            this.activePaymentMethod === ETAPaymentMethod.Card &&
            (checkoutStore.isAddNewCard || !checkoutStore.paymentsMethodId)
          ) {
            return response;
          }
        }

        if (
          this.activePaymentSystem === PaymentSystem.adyen &&
          response.data.orders[0].payment.providerPaymentDetails
        ) {
          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.ApplePay
          ) {
            this.applePay(response);
            return;
          }

          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.GooglePay
          ) {
            this.googlePay(response);
            return;
          }

          if (
            this.activePaymentMethod === ETAPaymentMethod.Card &&
            (this.isAddNewCard || !this.paymentsMethodId)
          ) {
            return response;
          }
        }

        if (this.activePaymentSystem === PaymentSystem.stripe) {
          if (!response.data.orders[0]?.payment_intent) {
            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'stripe_payment_intent_missing',
                error: 'no_payment_intent',
                orderId: response.data.orders[0]?.order?.id,
              });
              Sentry.captureMessage(
                '[Checkout Flow] Missing Stripe payment intent',
                'error',
              );
            });
            this.setIsLoading(false);
            throw new Error('!response.data.orders[0]?.payment_intent');
          }

          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.ApplePay
          ) {
            this.applePay(response);
            return;
          }

          if (
            mainStore.isRN &&
            this.activePaymentMethod === MobilePaymentMethod.GooglePay &&
            mainStore.isGooglePay
          ) {
            this.googlePay(response);
            return;
          }

          if (
            this.activePaymentMethod === ETAPaymentMethod.Card &&
            this.activePaymentSystem === PaymentSystem.stripe
          ) {
            if (!stripe || !elements) {
              Sentry.withScope((scope) => {
                scope.setExtras({
                  stage: 'stripe_initialization_error',
                  error: 'stripe_not_found',
                  hasStripe: !!stripe,
                  hasElements: !!elements,
                });
                Sentry.captureMessage(
                  '[Checkout Flow] Stripe not initialized',
                  'error',
                );
              });
              throw new Error('Stripe not found');
            }
            let paymentMethod: { card: StripeCardElement } | string;
            if (this.payments && this.payments.length && !this.isAddNewCard) {
              // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
              paymentMethod = this.paymentsMethodId || this.payments[0].value;
            } else {
              paymentMethod = {
                card: elements.getElement(CardElement) as StripeCardElement,
              };
            }

            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'stripe_card_payment',
                orderId: response.data.orders[0]?.order?.id,
                isNewCard: this.isAddNewCard,
                hasExistingPayments: !!this.payments?.length,
              });
              Sentry.captureMessage(
                '[Checkout Flow] Starting Stripe card payment',
                'info',
              );
            });

            this.setPaymentMethod(paymentMethod);
            this.stripeConfirmCardPayment(response, paymentMethod, stripe);
            return;
          }
        }
      }

      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'cash_payment',
          orderId: response.data.orders[0]?.order?.id,
        });
        Sentry.captureMessage(
          '[Checkout Flow] Processing cash payment',
          'info',
        );
      });

      this.setPaymentMethod('');
      this.finalizePayment().catch((error) => error && console.error(error));
      return response;
    } catch (e) {
      if (e instanceof OrderErrorHandled) {
        this.setIsLoading(false);
        return null;
      }

      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'checkout_error',
          error: e,
          context: 'orderCheckout',
        });
        Sentry.captureMessage(
          '[Checkout Flow] Checkout process failed',
          'error',
        );
      });
      if (
        !this.isAddNewCard &&
        this.activePaymentSystem &&
        [PaymentSystem.verifone, PaymentSystem.pvit].includes(
          this.activePaymentSystem,
        )
      ) {
        const errorInstance = e as AxiosError<ApiErrorResponse>;
        mainStore.pushAlert('error', errorInstance.message);
      }

      throw new Error(
        e instanceof Error ? e.message : 'Unable to create order',
      );
    }
  }

  midtransPay(snapToken: string) {
    const setIsLoading = this.setIsLoading.bind(this);
    const finalizePayment = this.finalizePayment.bind(this);

    try {
      window?.snap.pay(snapToken, {
        onSuccess: () => {
          finalizePayment().then(() => {
            const storedPromocode = getStoredPromocode();
            if (
              cartStore.promocode.success &&
              cartStore.promocode.value === storedPromocode?.code
            ) {
              clearStoredPromocode();
            }
          });
        },
        onError: (result) => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              result,
            });
            window?.snap.hide();
            mainStore.pushAlert('error', t('errors:unknown'));
            setIsLoading(false);
          });
        },
        onClose: () => {
          setIsLoading(false);
        },
      });
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          e,
        });
        setIsLoading(false);
      });
    }
  }

  async applePay(newOrderResponse: NewOrderResponse) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        flow: 'apple_pay',
        orderId: newOrderResponse.data.orders[0]?.order?.id,
        paymentSystem: this.activePaymentSystem,
        orderAmount: cartStore.finalPrice,
        currency: orderStore.currency,
        cartItems: cartStore.cart.map((item) => ({
          sku: item.sku,
          name: item.name,
          count: item.count,
          price: item.price,
        })),
        deliveryFee: this.calculateFullDeliveryFee(),
        paymentIntent: newOrderResponse.data.orders[0]?.payment_intent,
        myFatoorahDetails:
          newOrderResponse.data.orders[0]?.payment?.myFatoorahPayment,
      });
      Sentry.captureMessage('[Payment Flow] Starting Apple Pay flow', 'info');
    });

    const deliveryFee = this.calculateFullDeliveryFee();

    if (this.activePaymentSystem === PaymentSystem.myfatoorah) {
      mainStore.sendToRN(
        'initApplePay',
        {
          params: {
            cartItems: cartStore.cart
              .map<Record<string, string>>((item) => {
                return {
                  label: item.name || '',
                  paymentType: 'Immediate',
                  amount: mainStore.convertPenceToPounds(
                    (item.discountPrice ? item.discountPrice : item.price) *
                      item.count,
                  ),
                };
              })
              .concat({
                label: company.name,
                amount: cartStore.finalPrice,
                paymentType: 'Immediate',
              }),
            country: 'GB',
            currency: orderStore.currency.toUpperCase(),
            shippingMethods: [
              {
                label: 'Delivery',
                amount: deliveryFee || '0',
                identifier: 'delivery',
              },
            ],
          },
          clientSecret: undefined,
        },
        (e) => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              response: e,
              paymentAmount: cartStore.finalPrice,
            });
            Sentry.captureMessage('[ApplePay] Success', 'debug');
          });
        },
        (e) => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              error: e,
              paymentAmount: cartStore.finalPrice,
            });
            Sentry.captureMessage('[ApplePay] Failed', 'warning');
          });
        },
      );
    }

    await this.processPayment(newOrderResponse, deliveryFee);
  }

  async processPayment(
    newOrderResponse: NewOrderResponse,
    deliveryFee: string,
  ) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        stage: 'process_payment_start',
        orderId: newOrderResponse.data.orders[0]?.order?.id,
        paymentSystem: this.activePaymentSystem,
        paymentMethod: this.activePaymentMethod,
        orderAmount: cartStore.finalPrice,
        currency: orderStore.currency,
        paymentIntent: newOrderResponse.data.orders[0]?.payment_intent,
        clientSecret:
          newOrderResponse.data.orders[0]?.payment_intent?.client_secret,
        deliveryFee,
        timestamp: new Date().toISOString(),
      });
      Sentry.captureMessage('[Payment Flow] Processing payment', 'info');
    });

    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    if (
      !order.payment_intent ||
      !order.payment_intent.client_secret ||
      !order.order
    ) {
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      this.setIsLoading(false);
      throw new Error('!order.payment_intent');
    }

    try {
      const paymentResult = await new Promise<
        'success' | 'failed' | 'canceled' | 'timeout'
      >((resolve) => {
        if (!order.payment_intent) {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'payment_intent_missing',
              orderId: order.order?.id,
            });
            Sentry.captureMessage(
              '[Payment Flow] Missing payment intent',
              'error',
            );
          });
          resolve('failed');
          return;
        }

        const timeout = setTimeout(() => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'payment_timeout',
              orderId: order.order?.id,
              timeout: PAYMENT_TIMEOUT / 1000 + 'sec',
              paymentIntentSecret: order.payment_intent?.client_secret,
            });
            Sentry.captureMessage('[Payment Flow] Payment timed out', 'error');
          });
          resolve('timeout');
        }, PAYMENT_TIMEOUT);

        mainStore.sendToRN(
          'initApplePay',
          {
            params: {
              cartItems: cartStore.cart
                .map<Record<string, string>>((item) => {
                  return {
                    label: item.name || '',
                    amount: mainStore.convertPenceToPounds(
                      (item.discountPrice ? item.discountPrice : item.price) *
                        item.count,
                    ),
                    paymentType: 'Immediate',
                  };
                })
                .concat({
                  label: company.name,
                  amount: cartStore.finalPrice,
                  paymentType: 'Immediate',
                }),
              country: 'GB',
              currency: orderStore.currency.toUpperCase(),
              shippingMethods: [
                {
                  label: 'Delivery',
                  amount: deliveryFee ?? '0',
                  identifier: 'delivery',
                },
              ],
            },
            paymentMethods: orderStore.paymentMethods,
            sessionData: order.payment.providerPaymentDetails,
            clientSecret: order.payment_intent.client_secret,
          },
          (e) => {
            clearTimeout(timeout);
            Sentry.withScope((scope) => {
              scope.setExtras({
                response: e,
                paymentAmount: cartStore.finalPrice,
              });
              Sentry.captureMessage('[ApplePay] Success', 'debug');
            });
            resolve('success');
          },
          (e) => {
            clearTimeout(timeout);
            Sentry.withScope((scope) => {
              scope.setExtras({
                error: e,
                paymentAmount: cartStore.finalPrice,
              });
              Sentry.captureMessage('[ApplePay] Failed', 'warning');
            });
            const isCanceled =
              e.error?.code === 'Canceled' ||
              e.error?.code === 'Cancelled' ||
              e.error?.errorCode === 'canceledByShopper';
            resolve(isCanceled ? 'canceled' : 'failed');
          },
        );
      });

      if (
        this.paymentIntent?.payment_intent?.client_secret !==
        order.payment_intent.client_secret
      ) {
        throw new Error(
          'this.paymentIntent?.payment_intent?.client_secret !== order.payment_intent.client_secret',
        );
      } else {
        if (paymentResult === 'success') {
          await this.finalizePayment().catch(
            (error) => error && console.error(error),
          );
        } else {
          if (paymentResult === 'canceled') {
            await orderStore
              .orderCancel(order.order.id)
              .catch((error) => error && console.error(error));
          }
          if (paymentResult === 'failed' || paymentResult === 'timeout') {
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            throw new Error('failed or timeout');
          }
        }
      }
      this.setIsLoading(false);
    } catch (error) {
      if (
        this.paymentIntent?.payment_intent?.client_secret ===
        order.payment_intent.client_secret
      ) {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
      }
      Sentry.withScope((scope) => {
        scope.setExtras({
          error,
        });
        Sentry.captureMessage('[ApplePay] Error exception', 'warning');
      });
    }
  }

  async googlePay(newOrderResponse: NewOrderResponse) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        flow: 'google_pay',
        orderId: newOrderResponse.data.orders[0]?.order?.id,
        paymentSystem: this.activePaymentSystem,
        orderAmount: cartStore.finalPrice,
        currency: orderStore.currency,
        cartItems: cartStore.cart.map((item) => ({
          sku: item.sku,
          name: item.name,
          count: item.count,
          price: item.price,
        })),
        paymentIntent: newOrderResponse.data.orders[0]?.payment_intent,
        clientSecret:
          newOrderResponse.data.orders[0]?.payment_intent?.client_secret,
      });
      Sentry.captureMessage('[Payment Flow] Starting Google Pay flow', 'info');
    });

    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    if (
      !order.payment_intent ||
      !order.payment_intent.client_secret ||
      !order.order
    ) {
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      this.setIsLoading(false);
      throw new Error('!order.payment_intent');
    }
    try {
      const paymentResult = await new Promise<
        'success' | 'failed' | 'canceled' | 'timeout'
      >((resolve) => {
        if (!order.payment_intent) {
          resolve('failed');
          return;
        }
        const timeout = setTimeout(() => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              timeout: PAYMENT_TIMEOUT / 1000 + 'sec',
              request: {
                paymentIntentSecret: order.payment_intent?.client_secret || '',
              },
            });
            Sentry.captureMessage('[GooglePay] Timeout 10min', 'warning');
          });
          resolve('timeout');
        }, PAYMENT_TIMEOUT);
        mainStore.sendToRN(
          'requestGooglePayment',
          {
            transaction: {
              totalPrice: cartStore.finalPrice,
              totalPriceStatus: 'FINAL',
              currencyCode: orderStore.currency.toUpperCase(),
            },
            merchantName: this.isRecipientValid
              ? this.recipientNameVal
              : this.userStore.fullName,
            clientSecret: order.payment_intent.client_secret,
            sessionData: order.payment.providerPaymentDetails,
            paymentMethods: orderStore.paymentMethods,
          },
          (e) => {
            clearTimeout(timeout);
            Sentry.withScope((scope) => {
              scope.setExtras({
                response: e,
                paymentAmount: cartStore.finalPrice,
              });
              Sentry.captureMessage('[GooglePay] Success', 'debug');
            });
            resolve('success');
          },
          (e) => {
            clearTimeout(timeout);
            Sentry.withScope((scope) => {
              scope.setExtras({
                error: e,
                paymentAmount: cartStore.finalPrice,
              });
              Sentry.captureMessage('[GooglePay] Failed', 'warning');
            });
            const isCanceled =
              e.confirmationError?.code === 'PAYMENT_RESULT_CANCELED' ||
              e.error?.code === 'Canceled' ||
              e.error?.code === 'Cancelled' ||
              e.error?.errorCode === 'canceledByShopper';
            resolve(isCanceled ? 'canceled' : 'failed');
          },
        );
      });
      if (
        this.paymentIntent?.payment_intent?.client_secret !==
        order.payment_intent.client_secret
      ) {
        throw new Error(
          'this.paymentIntent?.payment_intent?.client_secret !== order.payment_intent.client_secret',
        );
      } else {
        if (paymentResult === 'success') {
          await this.finalizePayment().catch(
            (error) => error && console.error(error),
          );
        } else {
          if (paymentResult === 'canceled') {
            await orderStore
              .orderCancel(order.order.id)
              .catch((error) => error && console.error(error));
          }
          if (paymentResult === 'failed' || paymentResult === 'timeout') {
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            throw new Error('failed or timeout');
          }
        }
      }
      this.setIsLoading(false);
    } catch (error) {
      if (
        this.paymentIntent?.payment_intent?.client_secret ===
        order.payment_intent.client_secret
      ) {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
      }
      Sentry.withScope((scope) => {
        scope.setExtras({
          error,
        });
        Sentry.captureMessage('[GooglePay] Error exception', 'warning');
      });
    }
  }

  async stripeConfirmCardPayment(
    newOrderResponse: NewOrderResponse,
    paymentMethod: { card: StripeCardElement } | string,
    stripe: Stripe,
  ) {
    const order = newOrderResponse.data.orders[0];

    if (!order) {
      throw new Error('No order data');
    }

    if (
      !order.payment_intent ||
      !order.payment_intent.client_secret ||
      !order.order
    ) {
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      this.setIsLoading(false);
      throw new Error('!order.payment_intent');
    }
    try {
      const paymentResult = await new Promise<
        PaymentIntentResult | 'failed' | 'timeout'
      >((resolve, reject) => {
        if (!order.payment_intent) {
          resolve('failed');
          return;
        }

        const timeoutFroze = setTimeout(() => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              timeout: PAYMENT_TIMEOUT / 1000 + 'sec',
              request: {
                paymentIntentSecret: order.payment_intent?.client_secret || '',
                paymentMethod,
              },
            });
            Sentry.captureMessage(
              '[Checkout] Stripe confirmCardPayment timeout 10min',
              'warning',
            );
          });
          resolve('timeout');
        }, PAYMENT_TIMEOUT);

        stripe
          .confirmCardPayment(order.payment_intent.client_secret || '', {
            payment_method: paymentMethod,
          })
          .then((e) => {
            clearTimeout(timeoutFroze);
            resolve(e);
          })
          .catch((e) => {
            clearTimeout(timeoutFroze);
            reject(e);
          });
      });

      if (
        this.paymentIntent?.payment_intent?.client_secret !==
        order.payment_intent.client_secret
      ) {
        throw new Error(
          'paymentIntent?.payment_intent?.client_secret !== order.payment_intent.client_secret',
        );
      }

      if (paymentResult === 'failed' || paymentResult === 'timeout') {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
        throw new Error('Invalid paymentResult');
      }

      if (paymentResult.error) {
        this.setError(paymentResult.error.message || '');
        Sentry.withScope((scope) => {
          scope.setExtras({
            response: paymentResult,
          });
          Sentry.captureMessage(
            '[Checkout] Stripe confirmCardPayment error',
            'warning',
          );
        });
        this.setIsLoading(false);
        return;
      }

      await this.finalizePayment();
      this.setIsLoading(false);
    } catch (error) {
      if (
        this.paymentIntent?.payment_intent?.client_secret ===
        order.payment_intent.client_secret
      ) {
        this.setError('' /*i18n.t('errors:paymentSystemError')*/);
        this.setIsLoading(false);
      }
      Sentry.withScope((scope) => {
        scope.setExtras({
          error,
        });
        Sentry.captureMessage(
          '[Checkout] Stripe confirmCardPayment exception',
          'warning',
        );
      });
    }
  }

  onPaymentSuccess(orderId: string) {
    if (this._processedOrderIds.has(orderId)) return;
    this._processedOrderIds.add(orderId);

    orderStore
      .updateOrdersAnalytics()
      .catch((error) => error && console.error(error));
    orderStore.setOrderStatus('accepted');
    orderStore.setIsFirstOrder(false);
    mainStore.setNewDeliveryMethodForPromocodePopover(null);
    orderStore.setActiveOrderProductCount(cartStore.totalCartCount);
    orderStore.setActiveOrderID(orderId);
    if (cartStore.promocode.coupon) {
      const promocode = cartStore.promocode.value.toUpperCase();
      if (
        PRESENT_PROMOCODES.includes(promocode) ||
        cartStore.promocode.discountLimit
      ) {
        cartStore.promotionStore.addUsedPromocodes(promocode);
      }
      mainStore.analytics.promocodeUsedCount += 1;
      mainStore.sendToRN('setUserProperties', {
        'Commerce: promocodes used': mainStore.analytics.promocodeUsedCount,
      });
    }
    this.purchaseCompletedAnalytics(orderId);
    orderStore.setActiveGift(null);
    orderStore.setGift(null);
    cartStore.emptySelectedItemsFromCart();
    this.setSeletectedDateExpress();
    cartStore.promotionStore.resetPromocode();
    this.setUseBonuses(false);
  }

  async finalizePayment() {
    if (
      !this.paymentIntent ||
      !this.paymentIntent.order ||
      !this.paymentIntent.payment_intent
    ) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'finalize_payment_start',
          error: 'missing_payment_intent',
          paymentIntent: this.paymentIntent,
          orderState: {
            hasOrder: !!this.paymentIntent?.order,
            hasPaymentIntent: !!this.paymentIntent?.payment_intent,
            orderId: this.paymentIntent?.order?.id || null,
          },
        });
        Sentry.captureMessage(
          '[Payment Flow] Cannot finalize payment - missing payment intent',
          'error',
        );
      });
      throw new Error('!this.paymentIntent');
    }

    const isCardPayment = (
      [
        ETAPaymentMethod.Card,
        AdditionalPaymentMethod.VisaMastercard,
      ] as PaymentMethod[]
    ).includes(this.activePaymentMethod || ETAPaymentMethod.Card);

    try {
      this.setError(null);
      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'payment_verification_start',
          orderId: this.paymentIntent?.order?.id || null,
          paymentSystem: this.activePaymentSystem,
          isCardPayment,
          paymentMethod: this.activePaymentMethod,
        });
        Sentry.captureMessage(
          '[Payment Flow] Starting payment verification',
          'info',
        );
      });

      let count =
        (this.activePaymentSystem === PaymentSystem.myfatoorah ||
          this.activePaymentSystem === PaymentSystem.adyen ||
          this.activePaymentSystem === PaymentSystem.amazon ||
          this.activePaymentSystem === PaymentSystem.verifone ||
          this.activePaymentSystem === PaymentSystem.octo) &&
        isCardPayment
          ? 60
          : 6;
      let bonuses = null;
      const waitingExpires = Date.now() + PAYMENT_WAITING_TIME * 1000;
      while (count > 0) {
        this.setIsLoading(true);
        if (!this.paymentIntent || !this.paymentIntent.order) {
          mainStore.sendToRN('closeInAppBrowser');
          this.setPaymentIntent(null);
          this.setError('' /*i18n.t('errors:paymentSystemError')*/);
          this.setIsLoading(false);
          throw new Error('!this.paymentIntent || !this.paymentIntent.order');
        }
        if (
          this.activePaymentSystem === PaymentSystem.myfatoorah &&
          isCardPayment &&
          Date.now() >= waitingExpires
        ) {
          mainStore.sendToRN('closeInAppBrowser');
          this.setPaymentIntent(null);
          this.setError('' /*i18n.t('errors:paymentSystemError')*/);
          this.setIsLoading(false);
          throw new Error('waiting expires');
        }
        const { address, status, received_bonuses } =
          await orderStore.checkOrderPayment(this.paymentIntent.order.id);

        if (address) {
          if (
            address.customer_address_id !==
            this.userStore.deliveryAddress?.addressId
          ) {
            this.userStore.setDeliveryAddress({
              addressId: address.customer_address_id,
              zip: address.zip,
              country: address.country_id || '',
              city: address.city,
              region: address.region,
              address1: address.address_1 || '',
              address2: address.address_2 ?? '',
              addressFloor: address.floor || '',
              addressBld: address.building || '',
              addressApt: address.apartment || '',
              type:
                address.type ||
                (company.canDefaultAddressBeEmpty ? '' : 'home'),
              shortAddress: address.address_1 || '',
              coordinates: {
                lat: address.latitude || 0,
                lng: address.longitude || 0,
              },
              placeId: address.place_id || '',
              comment: address.comment || '',
              instructions: address.instructions || [],
            });
          }
        }

        Sentry.withScope((scope) => {
          scope.setExtras({
            stage: 'payment_verification_check',
            orderId: this.paymentIntent?.order?.id || null,
            status,
            attemptsLeft: count,
            received_bonuses,
            timeUntilExpiry: waitingExpires - Date.now(),
          });
          Sentry.captureMessage(
            '[Payment Flow] Payment verification check',
            'info',
          );
        });

        bonuses = received_bonuses;
        count--;
        if (!status || ['created', 'reserved'].includes(status)) {
          if (this.activePaymentSystem === PaymentSystem.pvit) {
            break;
          }

          if (count) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            continue;
          } else {
            Sentry.withScope((scope) => {
              scope.setExtras({
                stage: 'payment_verification_failed',
                orderId: this.paymentIntent?.order?.id || null,
                finalStatus: status,
                error: 'max_retries_reached',
              });
              Sentry.captureMessage(
                '[Payment Flow] Payment verification failed - max retries reached',
                'error',
              );
            });
            mainStore.sendToRN('closeInAppBrowser');
            this.setPaymentIntent(null);
            this.setError('' /*i18n.t('errors:paymentSystemError')*/);
            this.setIsLoading(false);
            throw new Error('!status');
          }
        }

        if (status === 'cancelled') {
          const cancelledOrderId = this.paymentIntent?.order?.id;

          Sentry.withScope((scope) => {
            scope.setExtras({
              stage: 'payment_cancelled',
              orderId: cancelledOrderId || null,
              status,
            });
            Sentry.captureMessage(
              '[Payment Flow] Payment cancelled by user or system',
              'warning',
            );
          });
          mainStore.sendToRN('closeInAppBrowser');
          this.setPaymentIntent(null);
          this.setError('' /*i18n.t('errors:paymentSystemError')*/);
          this.setIsLoading(false);

          if (
            this.activePaymentSystem === PaymentSystem.pvit &&
            cancelledOrderId
          ) {
            this.setOrderId(cancelledOrderId);
            return;
          }

          throw new Error('cancelled');
        }
        break;
      }

      Sentry.withScope((scope) => {
        scope.setExtras({
          stage: 'payment_success',
          orderId: this.paymentIntent?.order?.id || null,
          bonuses,
          finalPrice: cartStore.finalPrice,
          promocode: cartStore.promocode.value,
        });
        Sentry.captureMessage(
          '[Payment Flow] Payment completed successfully',
          'info',
        );
      });

      mainStore.sendToRN('closeInAppBrowser');
      this.onPaymentSuccess(this.paymentIntent.order.id);
      this.setOrderId(this.paymentIntent.order.id);
      orderStore.setActiveOrderBonuses(bonuses);
      if (!this.userStore.deliveryAddress?.address1) {
        this.userStore.setDeliveryAddress1(this.addressVal);
      }
      if (!this.userStore.deliveryAddress?.address2) {
        this.userStore.setDeliveryAddress2(this.address2Val);
      }
      if (!this.userStore.deliveryAddress?.addressFloor) {
        this.userStore.setDeliveryAddressFloor(this.addressFloorVal);
      }
      if (!this.userStore.deliveryAddress?.addressBld) {
        this.userStore.setDeliveryAddressBld(this.addressBldVal);
      }
      if (!this.userStore.deliveryAddress?.addressApt) {
        this.userStore.setDeliveryAddressApt(this.addressAptVal);
      }
    } catch (e) {
      this.setIsLoading(false);
      this.setError('' /*i18n.t('errors:paymentSystemError')*/);
      throw e;
    }
  }

  async syncAddress() {
    try {
      if (this.userStore.deliveryAddress) {
        let isChangedAddress = false;
        if (this.commentVal !== this.userStore.deliveryAddress.comment) {
          isChangedAddress = true;
          runInAction(() => {
            if (!this.userStore.deliveryAddress) {
              return;
            }
            this.userStore.deliveryAddress.comment = this.commentVal;
          });
        }
        if (
          Object.values(this.instructionList).length !==
          this.userStore.deliveryAddress.instructions?.length
        ) {
          runInAction(() => {
            if (!this.userStore.deliveryAddress) {
              return;
            }
            if (
              cartStore.isAdultItemInCart &&
              Object.values(this.instructionList).length === 2 &&
              this.userStore.deliveryAddress.instructions?.length === 3
            ) {
              return;
            }
            isChangedAddress = true;
            this.userStore.deliveryAddress.instructions = Object.values(
              this.instructionList,
            );
          });
        }
        if (!this.userStore.deliveryAddress.addressId) {
          await this.addNewAddress().catch(
            (error) => error && console.error(error),
          );
        } else {
          if (isChangedAddress) {
            const isSuccess = await this.userStore
              .updateAddress(this.userStore.deliveryAddress.addressId, {
                comment: this.userStore.deliveryAddress.comment || '',
                instructions: this.userStore.deliveryAddress.instructions || [],
              })
              .catch((error) => error && console.error(error));
            if (isSuccess) {
              return;
            }
            await this.addNewAddress().catch(
              (error) => error && console.error(error),
            );
          }
        }
      }
    } catch (error) {
      error && console.error(error);
    }
  }

  async addNewAddress() {
    if (!this.userStore.deliveryAddress) {
      return;
    }
    try {
      const address = await this.userStore
        .addAddress({
          street_address_1: this.userStore.deliveryAddress.address1,
          street_address_2: this.userStore.deliveryAddress.address2,
          building: this.userStore.deliveryAddress.addressBld,
          floor: this.userStore.deliveryAddress.addressFloor,
          apartment: this.userStore.deliveryAddress.addressApt,
          city: this.userStore.deliveryAddress.city,
          postcode: this.userStore.deliveryAddress.zip,
          latitude: this.userStore.deliveryAddress.coordinates.lat,
          longitude: this.userStore.deliveryAddress.coordinates.lng,
          country: this.userStore.deliveryAddress.country,
          comment: this.userStore.deliveryAddress.comment || '',
          instructions: this.userStore.deliveryAddress.instructions || [],
          type:
            this.userStore.deliveryAddress.type ||
            (company.canDefaultAddressBeEmpty ? '' : 'home'),
        })
        .catch((error) => error && console.error(error));
      runInAction(() => {
        if (!this.userStore.deliveryAddress) {
          return;
        }
        this.userStore.deliveryAddress.addressId = address.id || null;
        this.userStore.setDeliveryAddress2(address.street_address_2);
      });
    } catch (error) {
      error && console.error(error);
    }
  }

  resetStore(opts?: { resetDeliveryMethod?: boolean }) {
    this.addressVal = '';
    this.phoneVal = '';
    this.isPhoneValid = true;
    this.isEmailSync = true;
    this.isPersonalDataSync = true;
    this.nameVal = '';
    this.emailVal = '';
    this.commentVal = '';
    this.recipientPhoneVal = '';
    this.isRecipientPhoneValid = false;
    this.recipientNameVal = '';
    this.recipientEmailVal = '';
    this.recipientTempPhoneVal = '';
    this.isRecipientTempPhoneValid = false;
    this.recipientTempNameVal = '';
    this.recipientTempEmailVal = '';
    this.agreeSaveCard = true;
    this.error = null;
    this.isLoading = false;
    this.isDisabled = true;
    this.isPersonalDataChanged = false;
    this.isShowRecipientPopover = false;
    this.isShowTimeoutPopover = false;
    this.isUnableConfirmCardPayment = false;
    this.isAddNewCard = false;
    this._isCardValid = false;
    this._isCardAdded = false;
    this.paymentIntent = null;
    this.paymentMethod = '';
    this.orderId = '';
    this.octoPaymentLink = null;
    this._sentAnalyticsOrderIds.clear();
    this.cancellationDelay = null;
    this.isThreeds2Active = false;
    this.instructionList = {};
    this.stateData = null;
    this.isRefreshed = false;
    this.isUpdatedDeliveryPopover = false;
    this.sessionId = undefined;
    this.isDeliverTogether = company.combineItemsByDefaut;
    this._isNewAdyenCard = null;
    this._isAdyenCardValid = null;

    const { resetDeliveryMethod = false } = opts || {};

    if (resetDeliveryMethod) {
      if (
        !this.availableDeliveryMethods.length ||
        this.availableDeliveryMethods.includes(
          ETADeliveryMethodType.JiffyDelivery,
        )
      ) {
        this.setDeliveryMethod(ETADeliveryMethodType.JiffyDelivery);
      } else {
        // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
        this.setDeliveryMethod(this.availableDeliveryMethods[0]);
      }
    }
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }

  private getExactAmountInstruction() {
    return 'Client will prepare the exact amount';
  }

  /**
   * Build cart items for wallet payments (Apple Pay, Google Pay)
   * Used for Payment Request API integration
   */
  private buildWalletCartItems() {
    return cartStore.cart
      .map<Record<string, string>>((item) => {
        return {
          label: item.name || '',
          amount: mainStore.convertPenceToPounds(
            (item.discountPrice ? item.discountPrice : item.price) * item.count,
          ),
          paymentType: 'Immediate',
        };
      })
      .concat({
        label: company.name,
        amount: cartStore.finalPrice,
        paymentType: 'Immediate',
      });
  }

  /**
   * Process wallet payment using Payment Request API
   * Returns the wallet payment result with token
   */
  private processWalletPaymentRequest(
    paymentMethod: 'applePay' | 'googlePay',
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const cartItems = this.buildWalletCartItems();
      const eventName =
        paymentMethod === 'applePay' ? 'initApplePay' : 'requestGooglePayment';

      const params =
        paymentMethod === 'applePay'
          ? {
              cartItems,
              country: 'GB',
              currency: orderStore.currency.toUpperCase(),
              shippingMethods: [
                {
                  label: 'Delivery',
                  amount: 0 ?? '0',
                  identifier: 'delivery',
                },
              ],
            }
          : {
              cartItems,
              merchantName: company.name,
              country: 'GB',
              currency: orderStore.currency.toUpperCase(),
            };

      mainStore.sendToRN(
        eventName,
        {
          params,
          usePaymentRequestAPI: true,
        },
        (response) => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              response,
              paymentAmount: cartStore.finalPrice,
            });
            Sentry.captureMessage(
              `[${
                paymentMethod === 'applePay' ? 'ApplePay' : 'GooglePay'
              }] Success`,
              'debug',
            );
          });
          resolve(response);
        },
        (error) => {
          Sentry.withScope((scope) => {
            scope.setExtras({
              error,
              paymentAmount: cartStore.finalPrice,
            });
            Sentry.captureMessage(
              `[${
                paymentMethod === 'applePay' ? 'ApplePay' : 'GooglePay'
              }] Failed`,
              'warning',
            );
          });
          reject(error);
        },
      );
    });
  }
}
